import logger from "../../utils/logger";
import React, { useState, useCallback } from "react";
import { Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../supabaseClient";
import useMarketingPopup from "../../hooks/useMarketingPopup";
import IncentiveOfferPopup from "./IncentiveOfferPopup";
import EmailSignupPopup from "./EmailSignupPopup";
import SmsSignupPopup from "./SmsSignupPopup";
import SpinWinPromoPopup from "./SpinWinPromoPopup";
import EventRequestPopup from "./EventRequestPopup";

export default function MarketingPopup() {
  const { config, visible, loading, dismiss, isLoggedIn } = useMarketingPopup();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (formData) => {
    if (!config) return false;
    setSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const payload = {
        popup_id: config.id,
        user_id: user?.id || null,
        page_path: window.location.pathname,
        name: formData.name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        whatsapp: formData.whatsapp || null,
        event_name: formData.event_name || null,
        location: formData.location || null,
        event_date: formData.event_date || null,
        free_text: formData.free_text || null,
        coupon_issued: config.coupon_code || null,
      };

      await supabase.from("popup_submissions").insert(payload);

      supabase.rpc("record_popup_conversion", { popup_id: config.id }).then(() => {}).catch(() => {});

      return true;
    } catch (err) {
      logger.error("Popup submission error:", err);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [config]);

  if (loading || !config || !visible) return null;

  const isCenterModal = config.display_position === "center_modal";

  // Render center modal popup types
  if (isCenterModal) {
    switch (config.popup_type) {
      case "email_signup":
        return (
          <EmailSignupPopup
            config={config}
            onDismiss={dismiss}
            onSubmit={handleSubmit}
            submitting={submitting}
            isLoggedIn={isLoggedIn}
          />
        );
      case "sms_signup":
        // SMS not yet available — fall through to email
        return (
          <EmailSignupPopup
            config={config}
            onDismiss={dismiss}
            onSubmit={handleSubmit}
            submitting={submitting}
            isLoggedIn={isLoggedIn}
          />
        );
      case "spin_win_promo":
        return (
          <SpinWinPromoPopup
            config={config}
            onDismiss={dismiss}
            onSubmit={handleSubmit}
            submitting={submitting}
            isLoggedIn={isLoggedIn}
          />
        );
      case "incentive_offer":
        return (
          <IncentiveOfferPopup
            config={config}
            onDismiss={dismiss}
            onSubmit={handleSubmit}
            submitting={submitting}
            isLoggedIn={isLoggedIn}
          />
        );
      case "event_request":
      default:
        return (
          <EventRequestPopup
            config={config}
            onDismiss={dismiss}
            onSubmit={handleSubmit}
            submitting={submitting}
            isLoggedIn={isLoggedIn}
          />
        );
    }
  }

  // Bottom-right / bottom-left card style (compact, non-modal)
  const positionClass = config.display_position === "bottom_left"
    ? "fixed bottom-4 left-4"
    : "fixed bottom-4 right-4";

  return (
    <Transition
      show={visible}
      enter="ease-out duration-300"
      enterFrom="opacity-0 translate-y-8"
      enterTo="opacity-100 translate-y-0"
      leave="ease-in duration-200"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-8"
    >
      <div className={`${positionClass} z-[999999] w-[360px] max-w-[calc(100vw-32px)]`}>
        <div className="bg-[#17131F] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors z-10"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          <div className="p-5">
            <div className="text-center mb-4 pr-6">
              <h3 className="text-white text-[17px] font-semibold leading-tight">
                {config.title}
              </h3>
              {config.subtitle && (
                <p className="text-white/50 text-[13px] mt-1">{config.subtitle}</p>
              )}
            </div>

            <div className="space-y-2.5">
              {config.show_email_field && (
                <input
                  className="bg-white/5 focus:bg-white/10 border border-white/10 focus:border-purple-500/60 rounded-lg px-3.5 py-2.5 w-full text-white text-[14px] placeholder-white/30 outline-none transition-all"
                  placeholder="Email address"
                  type="email"
                  id="mp-email"
                />
              )}
              {config.show_phone_field && (
                <input
                  className="bg-white/5 focus:bg-white/10 border border-white/10 focus:border-green-500/60 rounded-lg px-3.5 py-2.5 w-full text-white text-[14px] placeholder-white/30 outline-none transition-all"
                  placeholder="Phone number"
                  type="tel"
                  id="mp-phone"
                />
              )}
            </div>

            {config.incentive_type && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600/10 border border-purple-500/20">
                <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
                <span className="text-purple-300 text-[12px] font-medium">{config.incentive_value || config.incentive_type}</span>
              </div>
            )}

            <button
              onClick={async () => {
                const email = document.getElementById("mp-email")?.value;
                const phone = document.getElementById("mp-phone")?.value;
                await handleSubmit({ email, phone });
              }}
              disabled={submitting}
              className="mt-3 w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/40 disabled:cursor-not-allowed text-white text-[14px] font-medium py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {submitting && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              {config.button_text || "Submit"}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
}
